import React from "react";
import { MdChevronRight, MdEmojiEvents, MdHistory } from "react-icons/md";
import Card from "components/card";
import CardHeader from "../common/CardHeader";
import { UserChallenge } from "../../types/challenge";
import {
  activeChallenges,
  isChallengeActive,
} from "../../utils/challengeAccess";
import { badge, BADGE_PILL } from "../../utils/badgeColors";

interface UserChallengeAccessCardProps {
  // The raw list from the detailed user payload, active and inactive alike
  challenges: UserChallenge[] | undefined;
  onOpenChallenge?: (challengeId: number) => void;
}

const ROW_CLASS =
  "flex w-full items-center justify-between gap-3 rounded-md border p-3 text-left";

const ChallengeRow: React.FC<{
  challenge: UserChallenge;
  onOpen?: (challengeId: number) => void;
}> = ({ challenge, onOpen }) => {
  const isActive = isChallengeActive(challenge);
  const toneClass = isActive
    ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
    : "border-gray-200 dark:border-gray-700";
  const content = (
    <>
      <span className="flex min-w-0 items-center text-sm font-medium text-navy-700 dark:text-white">
        <MdEmojiEvents
          className={`mr-2 h-4 w-4 flex-shrink-0 ${
            isActive ? "text-green-600 dark:text-green-300" : "text-gray-400"
          }`}
        />
        <span className="truncate">
          {challenge.name} ({challenge.conducted_at_year})
        </span>
      </span>
      <span className="flex flex-shrink-0 items-center gap-2">
        <span
          className={`${BADGE_PILL} ${
            isActive ? badge("green") : badge("gray")
          }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
        {onOpen && <MdChevronRight className="h-5 w-5 text-gray-400" />}
      </span>
    </>
  );

  if (!onOpen) {
    return <div className={`${ROW_CLASS} ${toneClass}`}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(challenge.id)}
      className={`${ROW_CLASS} ${toneClass} transition-colors hover:border-biafit-primary dark:hover:border-biafit-primary`}
    >
      {content}
    </button>
  );
};

// Explains why a user with no subscription or trial still has training
// access: active challenges grant it, finished ones are kept as history.
// Renders nothing for users who were never in a challenge.
const UserChallengeAccessCard: React.FC<UserChallengeAccessCardProps> = ({
  challenges,
  onOpenChallenge,
}) => {
  const all = challenges ?? [];
  if (all.length === 0) {
    return null;
  }
  const active = activeChallenges({ challenges: all });
  const previous = all.filter((challenge) => !isChallengeActive(challenge));

  return (
    <Card extra="w-full p-3 md:p-6">
      <CardHeader
        title="Challenge Access"
        count={active.length}
        singular="Active Challenge"
      />

      {active.length > 0 ? (
        <ul className="mb-6 space-y-2">
          {active.map((challenge) => (
            <li key={challenge.id}>
              <ChallengeRow challenge={challenge} onOpen={onOpenChallenge} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          No active challenge is assigned to this user.
        </p>
      )}

      {previous.length > 0 && (
        <div>
          <div className="mb-4 flex items-center space-x-2">
            <MdHistory className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
              Previous Challenges
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({previous.length})
            </span>
          </div>
          <ul className="space-y-2">
            {previous.map((challenge) => (
              <li key={challenge.id}>
                <ChallengeRow challenge={challenge} onOpen={onOpenChallenge} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default UserChallengeAccessCard;
